import { MODULES } from '@kontor/shared';
import { modulePaths, moduleNav } from './modules/registry';
import { NAV_FALLBACK, NAV_GROUPS } from './navigation';
import { SETUP_MODULES } from './setupSections';

type ModuleDef = (typeof MODULES)[number];

export interface NavItem {
  /** Schlüssel des Bereichs: Pfad des Eintrags oder Schlüssel des Moduls (so steht er in den Layouts der Branchen) */
  key: string;
  label: string;
  path: string | null;
}
export interface NavGroupView {
  key: string;
  label: string;
  items: NavItem[];
}

/** Pfade der bereits umgesetzten Module. Alle anderen erscheinen als "in Vorbereitung". Neue Module tragen sich in modules/registry.tsx ein. */
export const ROUTES: Record<string, string> = { inquiries: '/anfragen', ...modulePaths };

/**
 * Die Gruppen und Einträge der Seitenleiste. Ein Eintrag steht dort, wenn sein Modul in `licensed` steht, und, bei Einträgen mit Recht, wenn `can` es erlaubt. Was in keiner Gruppe steht,
 * kommt unter „Weitere“, damit ein neues Modul nie unsichtbar bleibt. Ein Eintrag eines Moduls kann in einer anderen Gruppe stehen als das Modul (`group` am Eintrag): Debitoren und Kreditoren
 * gehören zu den Stammdaten, obwohl ihre Module Ausgangs- und Eingangsrechnungen unter Finanzen stehen. Mit `withSetup` steht die Einrichtung in der Gruppe System.
 * Dieselbe Funktion liefert den Katalog der Bereiche für die Layouts der Branchen (alle Module, alle Rechte).
 */
export function buildNavGroups(opts: { licensed: readonly ModuleDef[]; can: (permission: string) => boolean; withSetup: boolean }): NavGroupView[] {
  const { licensed, can, withSetup } = opts;
  const placed = new Set(NAV_GROUPS.flatMap((g) => g.modules));
  const allowed = (e: { permission?: string }) => !e.permission || can(e.permission);
  const groups = [...NAV_GROUPS, { ...NAV_FALLBACK, modules: licensed.filter((m) => !placed.has(m.key)).map((m) => m.key) }]
    .map((g) => {
      const modules = g.modules.flatMap((key) => licensed.filter((m) => m.key === key));
      // Einträge anderer Module, die in diese Gruppe gehören. Mit `after` stehen sie direkt hinter einem Modul der Gruppe, sonst am Ende.
      const foreign = licensed
        .filter((m) => !g.modules.includes(m.key))
        .flatMap((m) => (moduleNav[m.key] ?? []).filter((e) => e.group === g.key && allowed(e)).map((e) => ({ item: { key: e.path, label: e.label, path: e.path as string | null }, after: e.after })));
      const items: NavItem[] = [
        ...modules.flatMap((m) => {
          // Ein Modul kann mehrere Einträge haben („Aufgaben“ und „Kalender & Termine“)
          const entries = moduleNav[m.key];
          const own = entries
            ? entries.filter((e) => (!e.group || e.group === g.key) && allowed(e)).map((e) => ({ key: e.path, label: e.label, path: e.path as string | null }))
            : [{ key: m.key, label: m.label, path: ROUTES[m.key] ?? null }];
          return [...own, ...foreign.filter((f) => f.after === m.key).map((f) => f.item)];
        }),
        ...foreign.filter((f) => !f.after || !modules.some((m) => m.key === f.after)).map((f) => f.item),
      ];
      // Einträge mit `afterPath` rücken hinter den ersten sichtbaren Eintrag, den sie nennen
      const wanted = new Map<string, string[]>();
      for (const m of licensed) for (const e of moduleNav[m.key] ?? []) if (e.afterPath) wanted.set(e.path, e.afterPath);
      for (const [path, targets] of wanted) {
        const from = items.findIndex((i) => i.key === path);
        const targetKey = targets.find((k) => items.some((i) => i.key === k));
        if (from < 0 || !targetKey) continue;
        const [moved] = items.splice(from, 1);
        items.splice(items.findIndex((i) => i.key === targetKey) + 1, 0, moved!);
      }
      return { key: g.key, label: g.label, items };
    })
    .map((g) => (g.key === 'system' && withSetup ? { ...g, items: [...g.items, { key: '/einrichtung', label: 'Einrichtung', path: '/einrichtung' as string | null }] } : g))
    .filter((g) => g.items.length > 0);
  // Ohne Module in der Gruppe System steht die Einrichtung trotzdem dort
  if (withSetup && !groups.some((g) => g.key === 'system')) groups.push({ key: 'system', label: 'System', items: [{ key: '/einrichtung', label: 'Einrichtung', path: '/einrichtung' }] });
  return groups;
}

/** Alle Bereiche, die ein Layout ein- oder ausblenden kann: alle umgesetzten Module, ohne die Einrichtung (sie bleibt immer sichtbar) */
export function layoutCatalog(): NavGroupView[] {
  const licensed = MODULES.filter((m) => m.available && !SETUP_MODULES.has(m.key));
  return buildNavGroups({ licensed, can: () => true, withSetup: false })
    .map((g) => ({ ...g, items: g.items.filter((i) => i.path !== null) }))
    .filter((g) => g.items.length > 0);
}
